import { useState } from "react";
import { ListFilter, Lock, Pencil, Plus, Trash2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { EmptyState, ErrorState, HeaderRow, LoadingState, TableShell, TH } from "@/components/PageUi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCalendarDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import { AccountSelect, accountLabel, BooksScreen, money, num, TaxRateSelect, useChart, useTaxRates } from "./shared";
import type { BankLineDirection, Book, BookRule, BookRuleField, BookRuleOperator, LedgerAccount } from "./types";

const EITHER = "either";

export function BookRules() {
  const { canManage } = useBooks();
  const [editing, setEditing] = useState<BookRule | "new" | null>(null);
  return (
    <BooksScreen
      title="Categorisation rules"
      subtitle="Bank transactions a rule matches are filed under its account automatically, as they arrive. The first matching rule wins."
      actions={
        canManage ? (
          <Button size="sm" onClick={() => setEditing("new")}>
            <Plus className="size-4" /> New rule
          </Button>
        ) : undefined
      }
    >
      {(book) =>
        canManage ? (
          <RulesBody book={book} editing={editing} setEditing={setEditing} />
        ) : (
          <EmptyState icon={Lock} title="Owners and admins manage rules">
            Ask an owner or admin of your organisation to add or change a rule.
          </EmptyState>
        )
      }
    </BooksScreen>
  );
}

function describe(rule: BookRule) {
  const parts = [`${rule.field} ${rule.operator === "Contains" ? "contains" : "is"} "${rule.value}"`];
  if (rule.direction) parts.push(rule.direction === "In" ? "money in" : "money out");
  if (rule.minAmount != null && rule.maxAmount != null) parts.push(`${money(rule.minAmount)} to ${money(rule.maxAmount)}`);
  else if (rule.minAmount != null) parts.push(`at least ${money(rule.minAmount)}`);
  else if (rule.maxAmount != null) parts.push(`up to ${money(rule.maxAmount)}`);
  return parts.join(" · ");
}

function RulesBody({
  book,
  editing,
  setEditing,
}: {
  book: Book;
  editing: BookRule | "new" | null;
  setEditing: (rule: BookRule | "new" | null) => void;
}) {
  const { books } = useBooks();
  const chart = useChart();
  const rules = useLoad(() => books.GET("/v1/books/current/rules", {}), [books]);
  const names = new Map((chart.data ?? []).map((a) => [a.id, a]));
  const readOnly = book.status !== "Active" && book.status !== "Setup";
  const [busy, setBusy] = useState<string | null>(null);

  async function patch(rule: BookRule, body: { isEnabled: boolean }) {
    setBusy(rule.id);
    const { error } = await books.PATCH("/v1/books/current/rules/{id}", {
      params: { path: { id: rule.id } },
      body: { ...body, clearDirection: false, clearAmountRange: false, clearTaxRate: false },
    });
    setBusy(null);
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't update the rule."));
      return;
    }
    toast.success(body.isEnabled ? "Rule on. It runs over the transactions waiting for review." : "Rule off.");
    await rules.reload();
  }

  async function remove(rule: BookRule) {
    setBusy(rule.id);
    const { error } = await books.DELETE("/v1/books/current/rules/{id}", { params: { path: { id: rule.id } } });
    setBusy(null);
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't delete the rule."));
      return;
    }
    toast.success("Rule deleted. Lines it categorised stay as they are.");
    await rules.reload();
  }

  const list = rules.data ?? [];
  return (
    <>
      {rules.loading && !rules.data ? (
        <LoadingState label="Loading rules" />
      ) : rules.error ? (
        <ErrorState message={apiErrorMessage(rules.error, "Couldn't load the rules.")} onRetry={rules.reload} />
      ) : list.length === 0 ? (
        <EmptyState
          icon={ListFilter}
          title="No rules yet"
          action={
            readOnly ? undefined : (
              <Button size="sm" onClick={() => setEditing("new")}>
                <Plus className="size-4" /> New rule
              </Button>
            )
          }
        >
          Add one here, or tick "Always do this" when you categorise a group of transactions from one payee.
        </EmptyState>
      ) : (
        <TableShell>
          <Table>
            <TableHeader>
              <HeaderRow>
                <TableHead className={TH}>Rule</TableHead>
                <TableHead className={cn(TH, "hidden @3xl:table-cell")}>Files under</TableHead>
                <TableHead className={cn(TH, "text-right")}>Lines</TableHead>
                <TableHead className={TH}>On</TableHead>
                {!readOnly ? <TableHead className={TH} /> : null}
              </HeaderRow>
            </TableHeader>
            <TableBody>
              {list.map((r) => {
                const account = names.get(r.ledgerAccountId);
                return (
                  <TableRow key={r.id} className={cn(!r.isEnabled && "text-muted-foreground")}>
                    <TableCell className="w-full max-w-0">
                      <div className="truncate font-medium">{r.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{describe(r)}</div>
                      {/* In a narrow pane (chat open) the account folds under the rule. */}
                      <div className="mt-0.5 truncate text-xs @3xl:hidden">{account ? accountLabel(account) : "Account not in the chart"}</div>
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap text-xs @3xl:table-cell">
                      {account ? accountLabel(account) : "Account not in the chart"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">
                      <div>{num(r.hitCount)}</div>
                      {r.lastHitAt ? <div className="text-xs text-muted-foreground">last {formatCalendarDate(r.lastHitAt)}</div> : null}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={r.isEnabled}
                        disabled={readOnly || busy === r.id}
                        onCheckedChange={(on) => patch(r, { isEnabled: on })}
                        aria-label={`${r.name} on`}
                      />
                    </TableCell>
                    {!readOnly ? (
                      <TableCell className="w-px whitespace-nowrap text-right">
                        <Button size="icon" variant="ghost" aria-label={`Edit ${r.name}`} onClick={() => setEditing(r)}>
                          <Pencil className="size-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" aria-label={`Delete ${r.name}`} disabled={busy === r.id}>
                              <Trash2 className="size-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{r.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>
                                New lines are no longer filed by it. Lines it already categorised stay as they are.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep it</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(r)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableShell>
      )}
      <Sheet open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full gap-0 overflow-y-auto sm:max-w-lg">
          {editing ? (
            <RuleForm
              key={editing === "new" ? "new" : editing.id}
              rule={editing === "new" ? null : editing}
              chart={chart.data ?? []}
              onSaved={async () => {
                setEditing(null);
                await rules.reload();
              }}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}

const amount = (v: string) => (v.trim() === "" ? null : Number(v));

function RuleForm({ rule, chart, onSaved }: { rule: BookRule | null; chart: LedgerAccount[]; onSaved: () => Promise<void> }) {
  const { books } = useBooks();
  const taxRates = useTaxRates();
  const [name, setName] = useState(rule?.name ?? "");
  const [field, setField] = useState<BookRuleField>(rule?.field ?? "Description");
  const [operator, setOperator] = useState<BookRuleOperator>(rule?.operator ?? "Contains");
  const [value, setValue] = useState(rule?.value ?? "");
  const [direction, setDirection] = useState<BankLineDirection | typeof EITHER>(rule?.direction ?? EITHER);
  const [min, setMin] = useState(rule?.minAmount != null ? String(rule.minAmount) : "");
  const [max, setMax] = useState(rule?.maxAmount != null ? String(rule.maxAmount) : "");
  const [accountId, setAccountId] = useState<string | null>(rule?.ledgerAccountId ?? null);
  const [taxRateId, setTaxRateId] = useState<string | null>(rule?.accountingTaxRateId ?? null);
  const [priority, setPriority] = useState(rule?.priority != null ? String(rule.priority) : "");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function save() {
    if (!accountId) return;
    setBusy(true);
    setFailure(null);
    const fields = {
      name,
      field,
      operator,
      value,
      direction: direction === EITHER ? null : direction,
      minAmount: amount(min),
      maxAmount: amount(max),
      ledgerAccountId: accountId,
      accountingTaxRateId: taxRateId,
      // Blank: a new rule goes after the others, an edited one keeps its place.
      priority: priority.trim() === "" ? null : Math.trunc(Number(priority)),
    };
    const { error } = rule
      ? await books.PATCH("/v1/books/current/rules/{id}", {
          params: { path: { id: rule.id } },
          // Clears apply before the values, so a full edit can empty any optional condition.
          body: { ...fields, clearDirection: true, clearAmountRange: true, clearTaxRate: true },
        })
      : await books.POST("/v1/books/current/rules", { body: { ...fields, isEnabled: true } });
    setBusy(false);
    if (error) {
      const message = apiErrorMessage(error, "Couldn't save the rule.");
      setFailure(message);
      toast.error(message);
      return;
    }
    toast.success("Rule saved. It runs over the transactions waiting for review.");
    await onSaved();
  }

  return (
    <>
      <SheetHeader className="border-b">
        <SheetTitle>{rule ? "Edit rule" : "New rule"}</SheetTitle>
        <SheetDescription>Matching transactions are categorised to the account as they arrive, and those waiting now.</SheetDescription>
      </SheetHeader>
      <div className="space-y-4 p-4">
        {failure ? (
          <p role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            {failure}
          </p>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="rule-name">Name</Label>
          <Input id="rule-name" value={name} maxLength={128} placeholder="e.g. Tesco" onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="rule-field">When the</Label>
            <Select value={field} onValueChange={(v) => setField(v as BookRuleField)}>
              <SelectTrigger id="rule-field" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Description">Description</SelectItem>
                <SelectItem value="Counterparty">Counterparty</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rule-operator">&nbsp;</Label>
            <Select value={operator} onValueChange={(v) => setOperator(v as BookRuleOperator)}>
              <SelectTrigger id="rule-operator" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Contains">contains</SelectItem>
                <SelectItem value="Equals">is</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rule-value">Text</Label>
          <Input id="rule-value" value={value} maxLength={256} placeholder="e.g. TESCO" onChange={(e) => setValue(e.target.value)} />
          <p className="text-xs text-muted-foreground">Case doesn't matter. "Is" also ignores digits and punctuation.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rule-direction">Direction</Label>
          <Select value={direction} onValueChange={(v) => setDirection(v as BankLineDirection | typeof EITHER)}>
            <SelectTrigger id="rule-direction" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EITHER}>Money in or out</SelectItem>
              <SelectItem value="Out">Money out</SelectItem>
              <SelectItem value="In">Money in</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="rule-min">From amount (optional)</Label>
            <Input id="rule-min" inputMode="decimal" type="number" min={0} step="0.01" value={min} onChange={(e) => setMin(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rule-max">To amount (optional)</Label>
            <Input id="rule-max" inputMode="decimal" type="number" min={0} step="0.01" value={max} onChange={(e) => setMax(e.target.value)} />
          </div>
        </div>
        <AccountSelect
          id="rule-account"
          label="File under"
          value={accountId}
          accounts={chart.filter((a) => a.isActive !== false)}
          onChange={setAccountId}
        />
        <div className="space-y-1.5">
          <Label htmlFor="rule-vat">VAT</Label>
          <TaxRateSelect id="rule-vat" value={taxRateId} rates={taxRates.data ?? []} onChange={setTaxRateId} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rule-priority">Priority (optional)</Label>
          <Input id="rule-priority" inputMode="numeric" type="number" step="1" value={priority} placeholder="After the other rules" onChange={(e) => setPriority(e.target.value)} />
          <p className="text-xs text-muted-foreground">Lower numbers are tried first; the first matching rule wins.</p>
        </div>
        <Button onClick={save} disabled={!accountId || !name.trim() || !value.trim() || busy}>
          {busy ? <Spinner className="size-4" /> : null} Save rule
        </Button>
      </div>
    </>
  );
}
